//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
  Button,
  Form,
  Input,
  LinearProgress,
  Modal,
  Select,
} from "@gpstrackit/gtc-react-controls";

//Components
import {
  NotificationManager,
  NotificationContainer,
} from "Components/Notifications";

//Slice
import {
  manageBulkUpdateManageCrew,
  manageCreateEditManageCrew,
  manageSearchCrewsByUnit,
  reset,
} from "./redux/slice";
import { manageSearchUnits, reset as resetUnit } from "../../components/vehicles/redux/slice";

//Helpers
import { hasEmojis, noEmojisValue } from "../../../../../.common/utils/formValues";
import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const INIT_DATA = {
  unit: undefined,
  assignedCrews: [],
  firstName: undefined,
  lastName: undefined,
  crewsAvailableOnly: [],
};

const CreateEditManageCrew = ({ refresh , paginationUnits}) => {
  //States
  const [formData, setFormData] = useState(INIT_DATA);
  const { messages } = useIntl();

  //Form refs
  const createEditCrewFormRef = Form.useForm();
  const editUnitCrewFormRef = Form.useForm();

  //Redux
  const {
    crew,
    loading,
    error,
    updateManageCrewResult,
    open,
    unit,
    crewsAvailableOnly,
    isAssignToUnit,
  } = useSelector((state) => state.manageCreateEditManageCrewRedux);

  const  { unitsList, loadingUnits } = useSelector((state) => state.manageUnitsRedux);

  const dispatch = useDispatch();

  //Other hooks
  const title = useMemo(() => {
    //Edit Crew Member for...
    if (unit)
      return `${messages["manage_createEditCrew_EditForTitle"]} "${unit.label}"`;

    //Re-Assign/Assign "crew" to
    if (isAssignToUnit)
      return `${
        crew?.unit?.id
          ? messages["manage_createEditCrew_ReAssignTitle"]
          : messages["manage_createEditCrew_AssignTitle"]
      } "${crew.firstName} ${crew.lastName}" ${
        messages["manage_createEditCrew_ToTitle"]
      }`;

    //Edit / Assign Crew Member
    return crew?.firstName || crew?.lastName
      ? `${messages["manage_createEditCrew_EditTitle"]}: "${crew?.firstName} ${crew?.lastName}"`
      : `${messages["manage_createEditCrew_CreateTitle"]}`;
  }, [crew, unit, isAssignToUnit]);

  const nameFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  const areAllCrewsSelected = useMemo(() => {
    const totalCrewsAvailable = formData.crewsAvailableOnly?.length || 0;
    const totalAssignedCrews = formData.assignedCrews?.length || 0;

    return totalCrewsAvailable > 0 && totalCrewsAvailable === totalAssignedCrews;
  }, [formData.assignedCrews, formData.crewsAvailableOnly]);

  //Handlers and utils
  const handleClose = () => {
    setFormData(INIT_DATA);
    dispatch(reset());
  };

  const handleSuccess = () => {
    NotificationManager.success(
      crew?.firstName || crew?.lastName ? messages["manage_createEditCrew_success_edit"] : messages["manage_createEditCrew_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  };

  const handleUpdateManageCrewResult = useCallback(() => {
    const firstName = formData.firstName != null ? String(formData.firstName).trim() : "";
    const lastName = formData.lastName != null ? String(formData.lastName).trim() : "";
    if ((!firstName || !lastName) && !unit) return;
    if (!unit) {
      const { unit: unitData } = formData;
      dispatch(
        manageCreateEditManageCrew({
          id: crew?.id,
          firstName,
          lastName,
          unitId: unitData?.id || 0,
        })
      );
      return;
    }
    const { assignedCrews } = formData;
    const bulkData = {
      crewIds: assignedCrews.map((crew) => crew.id),
      unitId: unit.id,
    };
    dispatch(manageBulkUpdateManageCrew(bulkData));
  }, [formData, crew, unit, isAssignToUnit]);

  //Effects
  useEffect(() => {
    if (!updateManageCrewResult) return;
    handleSuccess();
  }, [updateManageCrewResult]);

  useEffect(() => {
    if (!unit || !unit?.crews || loading !== false || loadingUnits) return;
    setFormData((currentFormData) => {
      const newCrews = crewsAvailableOnly.map((crew) => ({
        ...crew,
        name: crew.firstName + " " + crew.lastName,
      }));
      const assignedCrews = unit?.crews?.map((crew) => ({
        ...crew,
        name: crew.firstName + " " + crew.lastName,
      }));
      return {
        ...currentFormData,
        crewsAvailableOnly: [...newCrews, ...assignedCrews],
        assignedCrews,
      };
    });
  }, [unit, crewsAvailableOnly, loading, loadingUnits]);

  useEffect(() => {
    if (!unit) return;
    dispatch(
      manageSearchCrewsByUnit({
        conditions: [{ fields: ["unitId"], terms: [0], exact: true }],
      })
    );
  }, [unit]);

  useEffect(() => {
    if (!open || !crew) return;
    const { firstName, lastName } = crew;
    const defaultData = {
      firstName,
      lastName,
      unit: crew.unit || null,
    };
    setTimeout(() => createEditCrewFormRef.setValues(defaultData), 100);
    setFormData(defaultData);
  }, [crew, open]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (
      error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1011)
    ) {
      errorMessage =
        messages["manage_createEditCrew_duplicatedCrewErrorMessage"];
    }
    NotificationManager.error(
      errorMessage,
      messages["error"],
      3000,
      null,
      null,
      "warning"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (unit) return
    if (!Object.values(formData).some(value => value && hasEmojis(value))) return
    const newFormData = { ...formData };
    Object.entries(formData).forEach(([key, value]) => {
      if (hasEmojis(value)) newFormData[key] = noEmojisValue(value);
    })
    setTimeout(() => {
      createEditCrewFormRef.setValues(newFormData);
      setFormData(newFormData);
    }, 100)
  }, [formData, unit]);

  useEffect(() => {
    if (!open) return;
    dispatch(manageSearchUnits({}));
  }, [open]);

  return (
    <Modal
      title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              if (unit) {
                editUnitCrewFormRef?.submit();
                return;
              }
              createEditCrewFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        {unit ? (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "100%",
                marginBottom: "10px"
              }}
            >
              <Button
                isDisabled={!formData.crewsAvailableOnly?.length}
                size="small"
                variant={areAllCrewsSelected ? "warning" : "green"}
                onClick={(e) => {
                  e.preventDefault();
                  setFormData((currentFormData) => ({
                    ...currentFormData,
                    assignedCrews: areAllCrewsSelected
                      ? []
                      : currentFormData.crewsAvailableOnly,
                  }));
                }}
              >
                {areAllCrewsSelected ? messages["remove_all"] : messages["select_all"]}
              </Button>
            </div>

            <Form
              ref={editUnitCrewFormRef.ref}
              loading={loading}
              onValidSubmit={() => handleUpdateManageCrewResult()}
              onChangeValues={() => {}}
            >
              <span style={{ fontWeight: "bold" }}>
                {messages["manage_createEditCrew_members"]}
              </span>
              <Select
                id="crewsAvaibleOnly"
                label={messages["manage_createEditCrew_CrewInputLabel"]}
                placeholder={messages["manage_createEditCrew_CrewInputLabel"]}
                options={formData.crewsAvailableOnly || []}
                textField="name"
                valueField="id"
                value={formData.assignedCrews}
                onChange={(values) => {
                  const assignedCrews = values || [];
                  setFormData({ ...formData, assignedCrews });
                }}
                isMulti
                showPillsOutside
              />
            </Form>
          </>
        ) : (
          <Form
            ref={createEditCrewFormRef.ref}
            loading={loading || loadingUnits}
            onValidSubmit={() => handleUpdateManageCrewResult()}
            onChangeValues={() => {}}
          >
            {!isAssignToUnit ? (
              <>
                <Form.Item
                  name="firstName"
                  label={messages["manage_createEditCrew_FirstNameInputLabel"]}
                  rules={nameFieldRules}
                >
                  <Input
                    id="firstName"
                    type="text"
                    placeholder={messages["manage_createEditCrew_FirstNameInputLabel"]}
                    onChange={(value) => {
                      setFormData((currentFormData) => {
                        const newFormData = { ...currentFormData, firstName: value };
                        createEditCrewFormRef.setValues(newFormData);
                        return newFormData;
                      });
                    }}
                    maxLength={20}
                    value={formData.firstName}
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={messages["manage_createEditCrew_LastNameInputLabel"]}
                  rules={nameFieldRules}
                >
                  <Input
                    id="lastName"
                    type="text"
                    placeholder={messages["manage_createEditCrew_LastNameInputLabel"]}
                    onChange={(value) => {
                      const newFormData = { ...formData, lastName: value };
                      setFormData(newFormData);
                      createEditCrewFormRef.setValues(newFormData);
                    }}
                    maxLength={20}
                    value={formData.lastName}
                  />
                </Form.Item>{" "}
              </>
            ) : (
              <></>
            )}
            {!crew || isAssignToUnit ? (
              <>
                <Select
                  id="unit"
                  label={messages["manage_createEditCrew_VehicleInputLabel"]}
                  placeholder={messages["manage_createEditCrew_VehicleInputPlaceholder"]}
                  loading={loadingUnits}
                  options={unitsList}
                  textField="label"
                  valueField="id"
                  value={formData.unit?.id ? formData.unit : null}
                  onChange={(value) => setFormData({ ...formData, unit: value })}
                  isClearable
                  isSearchable
                />
                {loadingUnits && <LinearProgress />}
              </>
            ) : (
              <></>
            )}
          </Form>
        )}
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageCrew;
